import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Lock, Eye, EyeOff, AlertTriangle, Mail } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from '@/lib/authFetch';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Estado para primeiro acesso
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao fazer login');
      }

      // Verifica se é primeiro acesso
      if (data.primeiro_acesso) {
        setPendingEmail(data.email);
        setShowVerificationModal(true);
        toast({
          title: "Primeiro Acesso",
          description: "Um código de verificação foi enviado para seu e-mail.",
        });
        return;
      }
      
      // Login normal
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      
      toast({
        title: "Login Realizado",
        description: `Bem-vindo, ${data.user.nome}!`,
      });
      navigate('/admin');
    } catch (error: any) {
      toast({
        title: "Erro de Autenticação",
        description: error.message || "Email ou senha incorretos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: "Código Inválido",
        description: "O código deve ter 6 dígitos",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/verify-first-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail, code: verificationCode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Código inválido');
      }

      // Login bem-sucedido após verificação
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      
      setShowVerificationModal(false);
      
      toast({
        title: "Verificação Concluída!",
        description: `Bem-vindo, ${data.user.nome}!`,
      });
      navigate('/admin');
    } catch (error: any) {
      toast({
        title: "Erro na Verificação",
        description: error.message || "Código inválido",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.primeiro_acesso) {
        toast({
          title: "Código Reenviado",
          description: "Um novo código foi enviado para seu e-mail.",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível reenviar o código",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-protec-dark via-protec-dark-light to-protec-dark-lighter flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Protec Bens" 
              className="h-16 w-16"
            />
          </div>
          <h1 className="text-3xl font-bold gradient-text mb-2">PROTEC BENS</h1>
          <p className="text-protec-gold-light">Área Administrativa Restrita</p>
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mt-2">
            <Shield className="w-3 h-3 mr-1" />
            Acesso Exclusivo
          </Badge>
        </div>

        <Card className="bg-protec-dark-light border-protec-gold/30 shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-white flex items-center justify-center gap-2">
              <Lock className="w-5 h-5 text-protec-gold" />
              Autenticação Administrativa
            </CardTitle>
            <CardDescription className="text-gray-300">
              Entre com suas credenciais administrativas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div>
                <Label htmlFor="admin-email" className="text-gray-300">Email Administrativo</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-protec-dark border-protec-gold/30 text-white"
                  placeholder="admin@protecbens.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-gray-300">Senha</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-protec-dark border-protec-gold/30 text-white pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-gold text-black font-semibold hover:shadow-gold"
                disabled={isLoading}
              >
                {isLoading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
          >
            Voltar ao Site Principal
          </Button>
        </div>
      </div>

      {/* Modal de Verificação do Primeiro Acesso */}
      <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-protec-gold" />
              Verificação de Primeiro Acesso
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-protec-gold/20 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-protec-gold" />
              </div>
              <p className="text-gray-300">
                Este é seu primeiro acesso ao sistema.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Um código de verificação foi enviado para:
              </p>
              <p className="text-protec-gold font-semibold">{pendingEmail}</p>
            </div>

            <div>
              <Label>Código de Verificação (6 dígitos)</Label>
              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="bg-protec-dark border-protec-gold/30 text-white text-center text-2xl tracking-widest mt-2"
                maxLength={6}
              />
            </div>

            <Button 
              onClick={handleVerifyCode}
              disabled={isVerifying || verificationCode.length !== 6}
              className="w-full bg-protec-gold text-black font-semibold hover:bg-protec-gold-light"
            >
              {isVerifying ? 'Verificando...' : 'Verificar e Entrar'}
            </Button>

            <div className="text-center">
              <Button 
                variant="ghost" 
                className="text-gray-400 hover:text-protec-gold text-sm"
                onClick={handleResendCode}
              >
                Reenviar código
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogin;
